import { generateToken } from "./GenerateToken";

export const KINPOS_INTEGRATION_ID = 10;
export const KINPOS_CONTRACT = "smartpay.kinpos.transaction.v1";
export const KINPOS_RESULT_EVENT = "smartpay:card-payment-result";
export const KINPOS_TIMEOUT_MS = 45000;
export const KINPOS_PENDING_STORAGE_KEY = "smartpay.kinpos.pending.v1";
export const KINPOS_PROCESSED_STORAGE_KEY = "smartpay.kinpos.processed.v1";

const APPROVED_STATUSES = new Set(["approved", "approve", "success", "successful"]);
const DECLINED_STATUSES = new Set(["declined", "rejected", "denied"]);
const CANCELLED_STATUSES = new Set(["cancelled", "canceled", "voided"]);
const ERROR_STATUSES = new Set(["error", "failed", "failure"]);
const UNCERTAIN_STATUSES = new Set(["timeout", "timed_out", "expired", "not_found", "unknown"]);
const NON_TERMINAL_STATUSES = new Set(["pending", "processing", "accepted", "started"]);
const TERMINAL_STATUSES = new Set(["approved", "declined", "cancelled", "error"]);
const NON_APPROVED_TERMINAL_STATUSES = new Set(["declined", "cancelled", "error"]);
const AMOUNT_FIELDS = ["base", "tax1", "tax2", "tax3", "tax4", "tip"];
const CARD_NETWORK_PAYMENT_IDS = { clave: 2, mastercard: 3, visa: 5 };
const KINPOS_VOUCHER_WIDTH = 40;
const KINPOS_VOUCHER_LINE = "=".repeat(KINPOS_VOUCHER_WIDTH);
const KINPOS_VOUCHER_SEPARATOR = "-".repeat(KINPOS_VOUCHER_WIDTH);

const parseJsonValue = (value) => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

export const normalizeKinposPaymentStatus = (value) => {
  const status = parseJsonValue(value);
  if (!status || typeof status !== "object" || Array.isArray(status)) return null;
  return {
    provider: sanitizeText(status.provider, 32),
    installed: status.installed === true,
    busy: status.busy === true,
    package: sanitizeText(status.package, 128),
    transaction_contract: sanitizeText(status.transaction_contract, 128),
    supported_operations: Array.isArray(status.supported_operations)
      ? status.supported_operations
        .map((operation) => sanitizeText(operation, 32)?.toLowerCase())
        .filter(Boolean)
      : [],
  };
};

const sanitizeText = (value, maxLength = 256) => {
  if (value === undefined || value === null) return undefined;
  const text = String(value).trim();
  return text ? text.slice(0, maxLength) : undefined;
};

const normalizeComparableText = (value) => String(value ?? "")
  .trim()
  .toLowerCase()
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9]/g, "");

const normalizeCardNetwork = (value) => {
  const network = normalizeComparableText(value);
  if (network.includes("mastercard") || network === "master") return "mastercard";
  if (network.includes("visa")) return "visa";
  if (network.includes("clave")) return "clave";
  return network || null;
};

const sanitizeVoucherText = (value, maxLength = 80) => sanitizeText(value, maxLength)
  ?.replace(/[\r\n\t]+/g, " ")
  .replace(/\s+/g, " ");

const centerVoucherText = (value) => {
  const text = sanitizeVoucherText(value, KINPOS_VOUCHER_WIDTH) || "";
  const leftPadding = Math.max(0, Math.floor((KINPOS_VOUCHER_WIDTH - text.length) / 2));
  return `${" ".repeat(leftPadding)}${text}`;
};

const formatVoucherField = (label, value) => {
  const normalizedValue = sanitizeVoucherText(value);
  if (!normalizedValue) return null;
  const normalizedLabel = `${sanitizeVoucherText(label, 18)}:`;
  const padding = KINPOS_VOUCHER_WIDTH - normalizedLabel.length - normalizedValue.length;
  if (padding >= 1) return `${normalizedLabel}${" ".repeat(padding)}${normalizedValue}`;
  return `${normalizedLabel}\r\n${normalizedValue}`;
};

const getVoucherCardName = (result) => {
  const network = normalizeCardNetwork(result?.transaction?.card_network);
  if (network === "mastercard") return "MASTERCARD";
  if (network === "visa") return "VISA";
  if (network === "clave") return "CLAVE";
  const paymentMethodId = Number(result?.payment_method_id ?? result?.request?.payment_method_id);
  const knownNetwork = Object.entries(CARD_NETWORK_PAYMENT_IDS)
    .find(([, methodId]) => methodId === paymentMethodId)?.[0];
  return knownNetwork ? knownNetwork.toUpperCase() : `METODO ${paymentMethodId}`;
};

export const buildKinposVoucher = (result) => {
  if (!result || result.status !== "approved" || result.operation === "close") return "";
  const operation = result.operation === "void" ? "ANULACION" : "VENTA";
  const amount = sanitizeVoucherText(result.amount ?? result.request?.amount?.total, 32);
  const currency = sanitizeVoucherText(result.currency ?? result.request?.amount?.currency, 8) || "PAB";
  const fields = [
    formatVoucherField("OPERACION", operation),
    formatVoucherField("ESTADO", "APROBADO"),
    formatVoucherField("CODIGO", result.code || "00"),
    formatVoucherField("ORDEN", result.order_reference || result.request?.order_reference),
    formatVoucherField("TARJETA", getVoucherCardName(result)),
    amount ? formatVoucherField("TOTAL", `${currency} ${amount}`) : null,
    formatVoucherField("AUTORIZACION", result.transaction?.authorization),
    formatVoucherField(
      "RECIBO",
      result.transaction?.receipt || result.request?.transaction?.receipt_number
    ),
    formatVoucherField("STAN", result.transaction?.stan),
    formatVoucherField("RRN", result.transaction?.rrn),
  ].filter(Boolean);

  return [
    "",
    KINPOS_VOUCHER_LINE,
    centerVoucherText("KINPOS / PAX"),
    centerVoucherText(`${operation} APROBADA`),
    KINPOS_VOUCHER_SEPARATOR,
    ...fields,
    KINPOS_VOUCHER_SEPARATOR,
    centerVoucherText("COPIA DEL COMERCIO"),
    KINPOS_VOUCHER_LINE,
    "",
  ].join("\r\n");
};

export const getKnownKinposCardNetworkPaymentMethodId = (cardNetwork) => (
  CARD_NETWORK_PAYMENT_IDS[normalizeCardNetwork(cardNetwork)] ?? null
);

const createBridgeError = (code, message) => {
  const error = new Error(message);
  error.code = code;
  return error;
};

const createUniqueId = (prefix) => {
  return `${prefix}-${generateToken()}`;
};

const decimalToCents = (value, { positive = false } = {}) => {
  const textValue = typeof value === "string" ? value.trim().replace(",", ".") : value;
  const numericValue = Number(textValue);
  if (!Number.isFinite(numericValue) || numericValue < 0 || (positive && numericValue <= 0)) {
    throw createBridgeError("KINPOS_INVALID_AMOUNT", "El monto del pago KinPOS no es valido.");
  }
  return Math.round((numericValue + Number.EPSILON) * 100);
};

const centsToDecimal = (value) => (value / 100).toFixed(2);
const getTaxAmount = (tax) => (
  tax && typeof tax === "object" ? tax.total ?? tax.amount ?? tax.value ?? 0 : tax ?? 0
);

const normalizeAmountObject = (amount) => {
  if (!amount || typeof amount !== "object" || Array.isArray(amount)) {
    throw createBridgeError(
      "KINPOS_INVALID_AMOUNT",
      "KinPOS requiere un objeto amount con sus componentes fiscales."
    );
  }

  const totalCents = decimalToCents(amount.total, { positive: true });
  const componentCents = AMOUNT_FIELDS.reduce((result, fieldName) => ({
    ...result,
    [fieldName]: decimalToCents(amount[fieldName] ?? 0),
  }), {});
  const componentTotal = AMOUNT_FIELDS.reduce(
    (sum, fieldName) => sum + componentCents[fieldName],
    0
  );

  if (componentTotal !== totalCents) {
    throw createBridgeError(
      "KINPOS_AMOUNT_MISMATCH",
      "La suma fiscal del pago KinPOS no coincide con el total a cobrar."
    );
  }

  return {
    total: centsToDecimal(totalCents),
    ...AMOUNT_FIELDS.reduce((result, fieldName) => ({
      ...result,
      [fieldName]: centsToDecimal(componentCents[fieldName]),
    }), {}),
    currency: sanitizeText(amount.currency, 8) || "PAB",
  };
};

export const getPaymentIntegrationId = (payment) => {
  if (!payment || typeof payment !== "object") return null;
  const rawIntegrationId = payment.payment_integration_id
    ?? payment.paymentmethod_integration_id
    ?? payment.paymenthod_integration_id
    ?? payment.integration_id
    ?? payment.integration?.id;
  const integrationId = Number(rawIntegrationId);
  return Number.isFinite(integrationId) ? integrationId : null;
};

export const getPaymentMethodId = (payment) => {
  if (!payment || typeof payment !== "object") return null;
  const rawPaymentMethodId = payment.payment_id
    ?? payment.id
    ?? payment.payment_method_id
    ?? payment.paymenthod_id;
  const paymentMethodId = Number(rawPaymentMethodId);
  return Number.isFinite(paymentMethodId) ? paymentMethodId : null;
};

export const isKinposPaymentMethod = (payment) => (
  getPaymentIntegrationId(payment) === KINPOS_INTEGRATION_ID
);

export const isKinposIntegrationId = (integrationId) => (
  Number(integrationId) === KINPOS_INTEGRATION_ID
);

export const isKinposClosePending = (pendingPayment) => (
  pendingPayment?.mode === "close"
);

export const isKinposRegistrationRecoveryDraft = (pendingPayment, order) => (
  pendingPayment?.mode === "full"
  && pendingPayment?.state === "registration_failed"
  && order?.transaction_code !== undefined
  && order?.transaction_code !== null
  && String(pendingPayment.order_reference) === String(order.transaction_code)
);

export const getKinposPendingOperationLabel = (pendingPayment) => {
  if (pendingPayment?.mode === "close") return "Cierre KinPOS pendiente";
  if (pendingPayment?.mode === "void") return "Anulación KinPOS pendiente";
  return "Pago KinPOS pendiente";
};

export const isKinposEmptyInformZError = (error) => {
  const status = Number(error?.response?.status);
  if (status !== 400) return false;

  const responseData = error?.response?.data;
  try {
    const responseText = typeof responseData === "string"
      ? responseData
      : JSON.stringify(responseData);
    return /no orders found/i.test(String(responseText || ""));
  } catch (error) {
    return false;
  }
};

export const isGenericSettlementIntegrationId = (integrationId) => (
  ![4, 6, KINPOS_INTEGRATION_ID].includes(Number(integrationId))
);

export const formatKinposAmount = (amount) => centsToDecimal(
  decimalToCents(amount, { positive: true })
);

export const buildKinposFullAmount = ({ total, taxes = [], tip = 0, currency = "PAB" }) => {
  const totalCents = decimalToCents(total, { positive: true });
  const tipCents = decimalToCents(tip);
  const taxSlots = [0, 0, 0, 0];

  taxes.forEach((tax, index) => {
    const slotIndex = Math.min(index, taxSlots.length - 1);
    taxSlots[slotIndex] += decimalToCents(getTaxAmount(tax));
  });

  const taxTotalCents = taxSlots.reduce((sum, value) => sum + value, 0);
  const baseCents = totalCents - tipCents - taxTotalCents;
  if (baseCents < 0) {
    throw createBridgeError(
      "KINPOS_AMOUNT_MISMATCH",
      "Los impuestos y la propina exceden el total del pago KinPOS."
    );
  }

  return normalizeAmountObject({
    total: centsToDecimal(totalCents),
    base: centsToDecimal(baseCents),
    tax1: centsToDecimal(taxSlots[0]),
    tax2: centsToDecimal(taxSlots[1]),
    tax3: centsToDecimal(taxSlots[2]),
    tax4: centsToDecimal(taxSlots[3]),
    tip: centsToDecimal(tipCents),
    currency,
  });
};

export const buildKinposPartialAmount = (partialAmount, currency = "PAB") => {
  const total = formatKinposAmount(partialAmount);
  return normalizeAmountObject({
    total,
    base: total,
    tax1: "0.00",
    tax2: "0.00",
    tax3: "0.00",
    tax4: "0.00",
    tip: "0.00",
    currency,
  });
};

export const buildKinposRequest = ({
  payment,
  amount,
  orderReference,
  requestId = createUniqueId("payment"),
  tokenEcr = createUniqueId("ORDER"),
}) => {
  if (!isKinposPaymentMethod(payment)) {
    throw createBridgeError(
      "KINPOS_INVALID_INTEGRATION",
      "El metodo de pago seleccionado no pertenece a la integracion KinPOS."
    );
  }
  const paymentMethodId = getPaymentMethodId(payment);
  if (paymentMethodId === null) {
    throw createBridgeError(
      "KINPOS_INVALID_PAYMENT_METHOD",
      "El metodo de pago KinPOS no tiene un identificador valido."
    );
  }
  const normalizedOrderReference = sanitizeText(orderReference, 128);
  if (!normalizedOrderReference) {
    throw createBridgeError(
      "KINPOS_INVALID_ORDER_REFERENCE",
      "La orden no tiene una referencia valida para KinPOS."
    );
  }

  return {
    contract: KINPOS_CONTRACT,
    operation: "sale",
    request_id: sanitizeText(requestId, 128),
    payment_method_id: paymentMethodId,
    payment_integration_id: KINPOS_INTEGRATION_ID,
    token_ecr: sanitizeText(tokenEcr, 128),
    order_reference: normalizedOrderReference,
    amount: normalizeAmountObject(amount),
  };
};

export const buildKinposVoidRequest = ({
  payment,
  orderReference,
  receiptNumber,
  requestId = createUniqueId("void-order"),
  tokenEcr,
}) => {
  if (!isKinposPaymentMethod(payment)) {
    throw createBridgeError(
      "KINPOS_INVALID_INTEGRATION",
      "El metodo de pago seleccionado no pertenece a la integracion KinPOS."
    );
  }
  const paymentMethodId = getPaymentMethodId(payment);
  if (paymentMethodId === null) {
    throw createBridgeError(
      "KINPOS_INVALID_PAYMENT_METHOD",
      "El metodo de pago KinPOS no tiene un identificador valido."
    );
  }
  const normalizedOrderReference = sanitizeText(orderReference, 128);
  if (!normalizedOrderReference) {
    throw createBridgeError(
      "KINPOS_INVALID_ORDER_REFERENCE",
      "La orden no tiene una referencia valida para KinPOS."
    );
  }
  const normalizedReceiptNumber = sanitizeText(receiptNumber, 64);
  if (!normalizedReceiptNumber) {
    throw createBridgeError(
      "KINPOS_INVALID_RECEIPT",
      "La venta KinPOS original no contiene un numero de recibo valido para anular."
    );
  }

  return {
    contract: KINPOS_CONTRACT,
    operation: "void",
    request_id: sanitizeText(requestId, 128),
    payment_method_id: paymentMethodId,
    payment_integration_id: KINPOS_INTEGRATION_ID,
    token_ecr: sanitizeText(tokenEcr, 128) || sanitizeText(`VOID-${normalizedOrderReference}`, 128),
    order_reference: normalizedOrderReference,
    transaction: {
      receipt_number: normalizedReceiptNumber,
    },
  };
};

export const buildKinposCloseRequest = ({
  orderReference,
  requestId = createUniqueId("close-batch"),
}) => {
  const normalizedRequestId = sanitizeText(requestId, 128);
  if (!normalizedRequestId) {
    throw createBridgeError(
      "KINPOS_INVALID_REQUEST_ID",
      "El cierre KinPOS requiere un identificador unico valido."
    );
  }
  const normalizedOrderReference = sanitizeText(orderReference, 128);
  if (!normalizedOrderReference) {
    throw createBridgeError(
      "KINPOS_INVALID_ORDER_REFERENCE",
      "El cierre KinPOS requiere una referencia valida para el Informe Z."
    );
  }

  return {
    contract: KINPOS_CONTRACT,
    operation: "close",
    request_id: normalizedRequestId,
    payment_integration_id: KINPOS_INTEGRATION_ID,
    order_reference: normalizedOrderReference,
  };
};

const normalizeKinposCloseContext = (context, orderReference) => {
  if (!context || typeof context !== "object" || Array.isArray(context)) {
    throw createBridgeError(
      "KINPOS_CLOSE_CONTEXT_INVALID",
      "No se pudo conservar el contexto del Informe Z para recovery."
    );
  }
  const normalizedContext = {
    minDate: sanitizeText(context.minDate, 64),
    maxDate: sanitizeText(context.maxDate, 64),
    actualDate: sanitizeText(context.actualDate, 32),
    user_code: sanitizeText(context.user_code, 64),
    business_code: sanitizeText(context.business_code, 64),
    branchoffice_code: sanitizeText(context.branchoffice_code, 64),
    payment_integration_id: KINPOS_INTEGRATION_ID,
    order_reference: sanitizeText(orderReference, 128),
  };
  if (Object.values(normalizedContext).some((value) => value === undefined)) {
    throw createBridgeError(
      "KINPOS_CLOSE_CONTEXT_INVALID",
      "El contexto del Informe Z esta incompleto para recovery."
    );
  }
  return normalizedContext;
};

export const resolveActualKinposPaymentMethod = (
  selectedPaymentMethod,
  cardNetwork,
  availablePaymentMethods = []
) => {
  const normalizedNetwork = normalizeCardNetwork(cardNetwork);
  if (!normalizedNetwork) return selectedPaymentMethod;
  const kinposMethods = availablePaymentMethods.filter(isKinposPaymentMethod);
  const methodByName = kinposMethods.find((payment) => [
    payment.payment,
    payment.alias,
    payment.name,
  ].some((value) => normalizeCardNetwork(value) === normalizedNetwork));
  if (methodByName) return methodByName;

  const knownPaymentId = CARD_NETWORK_PAYMENT_IDS[normalizedNetwork];
  if (knownPaymentId !== undefined) {
    const methodById = kinposMethods.find(
      (payment) => getPaymentMethodId(payment) === knownPaymentId
    );
    if (methodById) return methodById;
  }
  return selectedPaymentMethod;
};

export const findKinposPaymentMethodById = (availablePaymentMethods = [], paymentMethodId) => (
  availablePaymentMethods.find((payment) => (
    isKinposPaymentMethod(payment) && getPaymentMethodId(payment) === Number(paymentMethodId)
  )) || null
);

const getEnvelopeSources = (value) => {
  const parsedValue = parseJsonValue(value);
  if (!parsedValue || typeof parsedValue !== "object") return [];
  return [parsedValue, parsedValue.result, parsedValue.data, parsedValue.payload]
    .map(parseJsonValue)
    .filter((source) => source && typeof source === "object");
};

const readResultField = (sources, fieldNames) => {
  for (const source of sources) {
    for (const fieldName of fieldNames) {
      if (source[fieldName] !== undefined && source[fieldName] !== null) return source[fieldName];
    }
  }
  return undefined;
};

export const normalizeKinposStatus = (status) => {
  const normalizedStatus = String(status ?? "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (APPROVED_STATUSES.has(normalizedStatus)) return { status: "approved" };
  if (DECLINED_STATUSES.has(normalizedStatus)) return { status: "declined" };
  if (CANCELLED_STATUSES.has(normalizedStatus)) return { status: "cancelled" };
  if (ERROR_STATUSES.has(normalizedStatus)) return { status: "error" };
  if (UNCERTAIN_STATUSES.has(normalizedStatus)) return { status: "uncertain", outcome: normalizedStatus };
  if (NON_TERMINAL_STATUSES.has(normalizedStatus)) return { status: "pending" };
  return { status: "uncertain", outcome: "unknown_status" };
};

export const normalizeKinposResult = (eventValue, request, { trustedQuery = false } = {}) => {
  const parsedEventValue = parseJsonValue(eventValue);
  const queriedFinalResult = trustedQuery
    && parsedEventValue
    && typeof parsedEventValue === "object"
    && String(parsedEventValue.state ?? "").toLowerCase() === "final"
    && parsedEventValue.result;
  const normalizedEventValue = queriedFinalResult || (
    trustedQuery && typeof eventValue === "string"
      && (!parsedEventValue || typeof parsedEventValue === "string")
      ? { status: parsedEventValue || eventValue }
      : eventValue
  );
  const rootSources = getEnvelopeSources(normalizedEventValue);
  if (rootSources.length === 0) return null;
  const contract = sanitizeText(readResultField(rootSources, ["contract", "schema"]), 128);
  if (contract && contract !== KINPOS_CONTRACT) return null;

  const requestId = sanitizeText(readResultField(rootSources, ["request_id", "requestId"]), 128);
  const tokenEcr = sanitizeText(readResultField(rootSources, ["token_ecr", "tokenEcr"]), 128);
  // request_id is authoritative. token_ecr is only a fallback when Android omits request_id.
  if (requestId) {
    if (requestId !== request.request_id) return null;
  } else if (tokenEcr) {
    if (tokenEcr !== request.token_ecr) return null;
  } else if (!trustedQuery) {
    return null;
  }

  const resultOperation = sanitizeText(readResultField(rootSources, ["operation"]), 32);
  if (request.operation === "close" && resultOperation !== "close") return null;
  const operation = resultOperation || request.operation;
  if (operation !== request.operation) return null;

  const normalizedStatus = normalizeKinposStatus(
    readResultField(rootSources, ["status", "result_status", "resultStatus", "state"])
  );
  const transactionSources = rootSources
    .map((source) => parseJsonValue(source.transaction))
    .filter((source) => source && typeof source === "object");
  const transaction = {
    authorization: sanitizeText(readResultField(transactionSources, ["authorization"]), 64),
    receipt: sanitizeText(readResultField(transactionSources, ["receipt"]), 64),
    stan: sanitizeText(readResultField(transactionSources, ["stan"]), 32),
    rrn: sanitizeText(readResultField(transactionSources, ["rrn"]), 64),
    card_network: sanitizeText(readResultField(transactionSources, ["card_network"]), 64),
  };

  const requestAmount = request.amount && typeof request.amount === "object"
    ? request.amount
    : null;
  const normalizedResult = {
    ...normalizedStatus,
    contract: contract || KINPOS_CONTRACT,
    success: readResultField(rootSources, ["success"]) === true,
    code: sanitizeText(readResultField(rootSources, ["code"]), 64),
    message: sanitizeText(readResultField(rootSources, ["message"])),
    provider: sanitizeText(readResultField(rootSources, ["provider"]), 64),
    request_id: requestId || request.request_id,
    token_ecr: tokenEcr || request.token_ecr,
    operation,
    payment_method_id: Number(readResultField(rootSources, ["payment_method_id"]) ?? request.payment_method_id),
    payment_integration_id: Number(readResultField(rootSources, ["payment_integration_id"]) ?? request.payment_integration_id),
    order_reference: sanitizeText(readResultField(rootSources, ["order_reference"]), 128) || request.order_reference,
    transaction,
    request,
  };
  const amount = sanitizeText(readResultField(rootSources, ["amount"]), 32) || requestAmount?.total;
  const currency = sanitizeText(readResultField(rootSources, ["currency"]), 8) || requestAmount?.currency;
  if (amount !== undefined) normalizedResult.amount = amount;
  if (currency !== undefined) normalizedResult.currency = currency;
  return normalizedResult;
};

const isRejectedAcknowledgement = (acknowledgement) => {
  if (acknowledgement === false || acknowledgement === "false") return true;
  const acknowledgementText = typeof acknowledgement === "string"
    ? acknowledgement.trim().toLowerCase()
    : "";
  if (["rejected", "error", "failed"].includes(acknowledgementText)) return true;
  const parsedAcknowledgement = parseJsonValue(acknowledgement);
  if (!parsedAcknowledgement || typeof parsedAcknowledgement !== "object") return false;
  if (parsedAcknowledgement.accepted === false || parsedAcknowledgement.success === false) return true;
  const status = String(parsedAcknowledgement.status ?? "").toLowerCase();
  return status === "rejected" || status === "error" || status === "failed";
};

export const toKinposPaymentRecord = (result) => {
  if (!result || result.status !== "approved" || result.operation !== "sale" || !result.request) return null;
  const record = {
    contract: KINPOS_CONTRACT,
    operation: "sale",
    status: "approved",
    request_id: result.request_id,
    order_reference: result.request.order_reference,
    amount: result.request.amount.total,
    currency: result.request.amount.currency,
    payment_method_id: result.request.payment_method_id,
    payment_integration_id: KINPOS_INTEGRATION_ID,
    transaction: { ...result.transaction },
    voucher: buildKinposVoucher(result),
  };
  Object.keys(record.transaction).forEach((fieldName) => {
    if (record.transaction[fieldName] === undefined) delete record.transaction[fieldName];
  });
  return record;
};

export const toKinposVoidRecord = (result) => {
  if (!result || result.status !== "approved" || result.operation !== "void" || !result.request) return null;
  const record = {
    contract: KINPOS_CONTRACT,
    operation: "void",
    status: "approved",
    request_id: result.request_id,
    order_reference: result.request.order_reference,
    payment_method_id: result.request.payment_method_id,
    payment_integration_id: KINPOS_INTEGRATION_ID,
    transaction: { ...result.transaction },
    voucher: buildKinposVoucher(result),
  };
  Object.keys(record.transaction).forEach((fieldName) => {
    if (record.transaction[fieldName] === undefined) delete record.transaction[fieldName];
  });
  return record;
};

export const toKinposCloseRecord = (result) => {
  if (
    !result
    || result.status !== "approved"
    || result.success !== true
    || result.operation !== "close"
    || !result.request
  ) return null;
  const transaction = { ...result.transaction };
  Object.keys(transaction).forEach((fieldName) => {
    if (transaction[fieldName] === undefined) delete transaction[fieldName];
  });
  const auditRecord = {
    contract: result.contract,
    provider: result.provider,
    operation: "close",
    status: result.status,
    code: result.code,
    message: result.message,
    request_id: result.request_id,
    order_reference: result.order_reference,
    ...(Object.keys(transaction).length > 0 ? { transaction } : {}),
  };
  Object.keys(auditRecord).forEach((fieldName) => {
    if (auditRecord[fieldName] === undefined) delete auditRecord[fieldName];
  });
  return {
    ...auditRecord,
    payment_integration_id: KINPOS_INTEGRATION_ID,
    voucher: JSON.stringify(auditRecord),
  };
};

const toPendingMetadata = (pendingPayment) => ({
  operation: pendingPayment.request.operation,
  request_id: pendingPayment.request.request_id,
  order_reference: pendingPayment.request.order_reference,
  payment_integration_id: pendingPayment.request.payment_integration_id,
  ...(pendingPayment.request.token_ecr ? { token_ecr: pendingPayment.request.token_ecr } : {}),
  ...(Number.isFinite(pendingPayment.request.payment_method_id)
    ? { payment_method_id: pendingPayment.request.payment_method_id }
    : {}),
  ...(pendingPayment.request.amount ? { amount: pendingPayment.request.amount } : {}),
  ...(pendingPayment.request.transaction ? { transaction: pendingPayment.request.transaction } : {}),
  created_at: pendingPayment.created_at,
  state: pendingPayment.state,
  mode: pendingPayment.mode,
  ...(TERMINAL_STATUSES.has(pendingPayment.terminalStatus)
    ? { terminal_status: pendingPayment.terminalStatus }
    : {}),
  ...(pendingPayment.context ? { context: pendingPayment.context } : {}),
});

export class KinposPaymentBridge {
  constructor({ windowObject, storage, timeoutMs = KINPOS_TIMEOUT_MS, now = () => Date.now() } = {}) {
    this.windowObject = windowObject;
    this.storage = storage;
    this.timeoutMs = timeoutMs;
    this.now = now;
    this.pendingPayment = null;
    this.listenerWindow = null;
    this.listenerDocument = null;
    this.subscribers = new Map();
    this.pendingStateSubscribers = new Set();
    this.processedRequestIds = new Set();
    this.recoveryPromise = null;
    this.handleResultEvent = this.handleResultEvent.bind(this);
    this.handleLifecycleRecovery = this.handleLifecycleRecovery.bind(this);
    this.restoreProcessedRequests();
    this.restorePendingPayment();
    this.ensureResultListener();
  }

  getWindow() {
    if (this.windowObject) return this.windowObject;
    return typeof window !== "undefined" ? window : null;
  }

  getStorage() {
    if (this.storage) return this.storage;
    return this.getWindow()?.localStorage || null;
  }

  isAvailable() {
    const windowObject = this.getWindow();
    return Boolean(windowObject?.AndroidBridge && typeof windowObject.AndroidBridge.chargeCard === "function");
  }

  getPaymentStatus() {
    const getKinposPaymentStatus = this.getWindow()?.AndroidBridge?.getKinposPaymentStatus;
    if (typeof getKinposPaymentStatus !== "function") return null;
    try {
      return normalizeKinposPaymentStatus(
        getKinposPaymentStatus.call(this.getWindow().AndroidBridge)
      );
    } catch (error) {
      return null;
    }
  }

  assertReady(operation) {
    if (!this.isAvailable()) {
      throw createBridgeError(
        "KINPOS_BRIDGE_UNAVAILABLE",
        "La integracion KinPOS/PAX no esta disponible en este dispositivo."
      );
    }
    const status = this.getPaymentStatus();
    if (!status) {
      throw createBridgeError(
        "KINPOS_STATUS_UNAVAILABLE",
        "Android no pudo confirmar el estado de KinPOS/PAX."
      );
    }
    if (status.provider !== "kinpos" || status.transaction_contract !== KINPOS_CONTRACT) {
      throw createBridgeError(
        "KINPOS_CONTRACT_MISMATCH",
        "La version de la integracion KinPOS/PAX no es compatible."
      );
    }
    if (!status.installed) {
      throw createBridgeError(
        "KINPOS_APP_NOT_INSTALLED",
        "La aplicacion mPOS KinPOS no esta instalada en este dispositivo."
      );
    }
    if (!status.supported_operations.includes(operation)) {
      throw createBridgeError(
        "KINPOS_OPERATION_UNSUPPORTED",
        `KinPOS/PAX no soporta la operacion ${operation}.`
      );
    }
    if (status.busy) {
      throw createBridgeError(
        "KINPOS_DEVICE_BUSY",
        "El terminal KinPOS/PAX ya tiene una operacion en proceso."
      );
    }
    return status;
  }

  hasPendingPayment() {
    this.reconcileProcessedPending();
    return this.pendingPayment !== null;
  }

  getPendingPayment() {
    this.reconcileProcessedPending();
    if (!this.pendingPayment) return null;
    return { ...toPendingMetadata(this.pendingPayment), restored: this.pendingPayment.restored };
  }

  isProcessed(requestId) {
    return this.processedRequestIds.has(requestId);
  }

  subscribe(mode, subscriber) {
    if (typeof mode === "function") {
      subscriber = mode;
      mode = "full";
    }
    if (typeof subscriber !== "function") return () => {};
    if (!this.subscribers.has(mode)) this.subscribers.set(mode, new Set());
    this.subscribers.get(mode).add(subscriber);
    if (
      this.pendingPayment?.mode === mode
      && this.pendingPayment.terminalResult
      && !this.pendingPayment.delivered
    ) {
      this.deliverTerminalResult(this.pendingPayment.terminalResult);
    }
    return () => this.subscribers.get(mode)?.delete(subscriber);
  }

  subscribePendingState(subscriber) {
    if (typeof subscriber !== "function") return () => {};
    this.pendingStateSubscribers.add(subscriber);
    subscriber(this.getPendingPayment());
    return () => this.pendingStateSubscribers.delete(subscriber);
  }

  notifyPendingState() {
    const pendingPayment = this.pendingPayment
      ? { ...toPendingMetadata(this.pendingPayment), restored: this.pendingPayment.restored }
      : null;
    this.pendingStateSubscribers.forEach((subscriber) => {
      try {
        subscriber(pendingPayment);
      } catch (error) {
        // A UI subscriber must not interrupt payment state persistence or recovery.
      }
    });
  }

  ensureResultListener() {
    const windowObject = this.getWindow();
    if (!windowObject || this.listenerWindow === windowObject) return;
    if (this.listenerWindow) {
      this.listenerWindow.removeEventListener(KINPOS_RESULT_EVENT, this.handleResultEvent);
      this.listenerWindow.removeEventListener("focus", this.handleLifecycleRecovery);
      this.listenerWindow.removeEventListener("pageshow", this.handleLifecycleRecovery);
      this.listenerDocument?.removeEventListener("visibilitychange", this.handleLifecycleRecovery);
    }
    windowObject.addEventListener(KINPOS_RESULT_EVENT, this.handleResultEvent);
    windowObject.addEventListener("focus", this.handleLifecycleRecovery);
    windowObject.addEventListener("pageshow", this.handleLifecycleRecovery);
    windowObject.document?.addEventListener("visibilitychange", this.handleLifecycleRecovery);
    this.listenerWindow = windowObject;
    this.listenerDocument = windowObject.document || null;
  }

  handleLifecycleRecovery(event) {
    if (
      event?.type === "visibilitychange"
      && this.listenerDocument?.visibilityState !== "visible"
    ) return;
    if (!this.pendingPayment) return;
    Promise.resolve(this.recover()).catch(() => {
      // recover() persists an uncertain state when Android cannot be queried safely.
    });
  }

  persistPendingPayment() {
    if (!this.pendingPayment) return;
    this.pendingPayment.revision = (Number(this.pendingPayment.revision) || 0) + 1;
    const storage = this.getStorage();
    if (storage) {
      try {
        storage.setItem(KINPOS_PENDING_STORAGE_KEY, JSON.stringify(toPendingMetadata(this.pendingPayment)));
      } catch (error) {
        // A storage failure must not reveal or log the bridge payload.
      }
    }
    this.notifyPendingState();
  }

  removePersistedPendingPayment() {
    try {
      this.getStorage()?.removeItem(KINPOS_PENDING_STORAGE_KEY);
    } catch (error) {
      // Keep the in-memory state authoritative for the current page.
    }
  }

  restoreProcessedRequests() {
    try {
      const values = parseJsonValue(this.getStorage()?.getItem(KINPOS_PROCESSED_STORAGE_KEY));
      if (Array.isArray(values)) {
        values.slice(-50).forEach((requestId) => this.processedRequestIds.add(String(requestId)));
      }
    } catch (error) {
      this.processedRequestIds = new Set();
    }
  }

  persistProcessedRequests() {
    try {
      const recentRequestIds = Array.from(this.processedRequestIds).slice(-50);
      this.getStorage()?.setItem(KINPOS_PROCESSED_STORAGE_KEY, JSON.stringify(recentRequestIds));
    } catch (error) {
      // Idempotency remains active in memory even if persistence is unavailable.
    }
  }

  reconcileProcessedPending() {
    if (!this.pendingPayment) return;
    this.restoreProcessedRequests();
    if (this.isProcessed(this.pendingPayment.request.request_id)) {
      this.clearPendingPayment();
    }
  }

  restorePendingPayment() {
    let metadata;
    try {
      metadata = parseJsonValue(this.getStorage()?.getItem(KINPOS_PENDING_STORAGE_KEY));
    } catch (error) {
      return;
    }
    if (
      !metadata
      || typeof metadata !== "object"
      || Number(metadata.payment_integration_id) !== KINPOS_INTEGRATION_ID
      || !metadata.request_id
      || this.isProcessed(metadata.request_id)
    ) return;

    try {
      const operation = ["sale", "void", "close"].includes(metadata.operation)
        ? metadata.operation
        : "sale";
      const request = {
        contract: KINPOS_CONTRACT,
        operation,
        request_id: sanitizeText(metadata.request_id, 128),
        payment_integration_id: KINPOS_INTEGRATION_ID,
        order_reference: sanitizeText(metadata.order_reference, 128),
      };
      const paymentMethodId = Number(metadata.payment_method_id);
      const tokenEcr = sanitizeText(metadata.token_ecr, 128);
      if (Number.isFinite(paymentMethodId)) request.payment_method_id = paymentMethodId;
      if (tokenEcr) request.token_ecr = tokenEcr;
      if (operation === "void") {
        const receiptNumber = sanitizeText(metadata.transaction?.receipt_number, 64);
        if (!receiptNumber) return;
        request.transaction = { receipt_number: receiptNumber };
      } else if (operation === "sale") {
        request.amount = normalizeAmountObject(metadata.amount);
      }
      if (!request.order_reference) return;
      if (operation !== "close" && (!request.token_ecr || !Number.isFinite(request.payment_method_id))) return;
      const persistedContext = metadata.context && typeof metadata.context === "object"
        ? metadata.context
        : null;
      const context = operation === "close"
        ? normalizeKinposCloseContext(persistedContext, request.order_reference)
        : persistedContext;
      const terminalStatus = sanitizeText(metadata.terminal_status, 32)?.toLowerCase();
      this.pendingPayment = {
        request,
        created_at: Number(metadata.created_at) || this.now(),
        state: sanitizeText(metadata.state, 64) || "pending",
        mode: operation === "close"
          ? "close"
          : operation === "void"
            ? "void"
            : metadata.mode === "mixed" ? "mixed" : "full",
        context,
        restored: true,
        terminalResult: null,
        terminalStatus: TERMINAL_STATUSES.has(terminalStatus) ? terminalStatus : null,
        delivered: false,
        revision: 0,
        timeoutId: null,
      };
      this.scheduleUiTimeout();
    } catch (error) {
      // Invalid persisted metadata is ignored; no raw Android data is stored here.
    }
  }

  scheduleUiTimeout() {
    if (!this.pendingPayment) return;
    clearTimeout(this.pendingPayment.timeoutId);
    if (["awaiting_registration", "awaiting_ack", "registration_failed", "uncertain"].includes(this.pendingPayment.state)) return;
    const elapsed = Math.max(0, this.now() - this.pendingPayment.created_at);
    const remaining = Math.max(0, this.timeoutMs - elapsed);
    this.pendingPayment.timeoutId = setTimeout(() => this.handleUiTimeout(), remaining);
  }

  notify(result) {
    if (!this.pendingPayment) return;
    const subscribers = this.subscribers.get(this.pendingPayment.mode) || new Set();
    subscribers.forEach((subscriber) => {
      Promise.resolve(subscriber(result)).catch(() => {
        this.markProcessingFailed(result.request_id);
      });
    });
  }

  notifyFirst(result) {
    if (!this.pendingPayment) return;
    const subscribers = this.subscribers.get(this.pendingPayment.mode) || new Set();
    const subscriber = subscribers.values().next().value;
    if (typeof subscriber !== "function") return;
    Promise.resolve(subscriber(result)).catch(() => {
      this.markProcessingFailed(result.request_id);
    });
  }

  handleUiTimeout() {
    if (!this.pendingPayment || TERMINAL_STATUSES.has(this.pendingPayment.state)) return;
    this.pendingPayment.state = "uncertain";
    this.persistPendingPayment();
    this.notify({
      status: "uncertain",
      outcome: "ui_timeout",
      code: "KINPOS_RESULT_UNCONFIRMED",
      message: "El resultado del pago aun no ha sido confirmado.",
      request_id: this.pendingPayment.request.request_id,
      request: {
        ...this.pendingPayment.request,
        mode: this.pendingPayment.mode,
        restored: this.pendingPayment.restored,
      },
    });
    if (typeof this.getWindow()?.AndroidBridge?.getCardPaymentState === "function") {
      this.recover();
    }
  }

  attachPendingContext(result) {
    return {
      ...result,
      request: {
        ...this.pendingPayment.request,
        mode: this.pendingPayment.mode,
        restored: this.pendingPayment.restored,
        ...(this.pendingPayment.context ? { context: this.pendingPayment.context } : {}),
      },
    };
  }

  deliverTerminalResult(result) {
    if (!this.pendingPayment || this.isProcessed(result.request_id) || this.pendingPayment.delivered) return false;
    const contextualResult = this.attachPendingContext(result);
    this.pendingPayment.terminalResult = contextualResult;
    this.pendingPayment.terminalStatus = result.status;
    const subscribers = this.subscribers.get(this.pendingPayment.mode);
    if (!subscribers || subscribers.size === 0) {
      this.pendingPayment.state = "result_received";
      this.persistPendingPayment();
      return false;
    }
    clearTimeout(this.pendingPayment.timeoutId);
    this.pendingPayment.delivered = true;
    this.pendingPayment.state = "processing_result";
    this.persistPendingPayment();
    this.notifyFirst(contextualResult);
    return true;
  }

  handleNormalizedResult(result) {
    if (!result || !this.pendingPayment) return null;
    if (TERMINAL_STATUSES.has(result.status)) {
      this.deliverTerminalResult(result);
      return result;
    }
    if (result.status === "pending") {
      if (this.pendingPayment.state !== "uncertain") this.pendingPayment.state = "pending";
      this.persistPendingPayment();
      this.notify(this.attachPendingContext(result));
      return result;
    }
    this.pendingPayment.state = "uncertain";
    this.persistPendingPayment();
    this.notify(this.attachPendingContext(result));
    return result;
  }

  handleResultEvent(event) {
    if (!this.pendingPayment) return;
    const eventValue = event?.detail ?? event?.data ?? event;
    const result = normalizeKinposResult(eventValue, this.pendingPayment.request);
    this.handleNormalizedResult(result);
  }

  async startOperation({ request, mode, context = null, preflightChecked = false }) {
    if (this.hasPendingPayment()) {
      throw createBridgeError("KINPOS_PAYMENT_PENDING", "Ya existe un pago KinPOS pendiente.");
    }
    if (!preflightChecked) this.assertReady(request.operation);

    this.ensureResultListener();
    this.pendingPayment = {
      request,
      created_at: this.now(),
      state: "pending",
      mode,
      context,
      restored: false,
      terminalResult: null,
      terminalStatus: null,
      delivered: false,
      revision: 0,
      timeoutId: null,
    };
    this.persistPendingPayment();
    this.scheduleUiTimeout();

    try {
      const acknowledgement = this.getWindow().AndroidBridge.chargeCard(JSON.stringify(request));
      if (isRejectedAcknowledgement(acknowledgement)) {
        throw createBridgeError("KINPOS_REQUEST_REJECTED", "Android no pudo iniciar la operacion KinPOS/PAX.");
      }
      return request;
    } catch (error) {
      this.clearPendingPayment();
      if (error?.code) throw error;
      throw createBridgeError(
        "KINPOS_INVOCATION_ERROR",
        "No se pudo solicitar la operacion al terminal KinPOS/PAX."
      );
    }
  }

  async charge({ payment, amount, orderReference, mode = "full" }) {
    const request = buildKinposRequest({ payment, amount, orderReference });
    return this.startOperation({
      request,
      mode: mode === "mixed" ? "mixed" : "full",
    });
  }

  async voidTransaction({ payment, orderReference, receiptNumber, context = null }) {
    const request = buildKinposVoidRequest({ payment, orderReference, receiptNumber });
    return this.startOperation({ request, mode: "void", context });
  }

  async closeBatch({ orderReference, context }) {
    if (this.hasPendingPayment()) {
      throw createBridgeError("KINPOS_PAYMENT_PENDING", "Ya existe una operacion KinPOS pendiente.");
    }
    this.assertReady("close");
    const request = buildKinposCloseRequest({ orderReference });
    const safeContext = normalizeKinposCloseContext(context, request.order_reference);
    return this.startOperation({
      request,
      mode: "close",
      context: safeContext,
      preflightChecked: true,
    });
  }

  async recover() {
    if (this.recoveryPromise) return this.recoveryPromise;
    const recoveryPromise = this.performRecovery();
    this.recoveryPromise = recoveryPromise;
    try {
      return await recoveryPromise;
    } finally {
      if (this.recoveryPromise === recoveryPromise) this.recoveryPromise = null;
    }
  }

  async performRecovery() {
    this.reconcileProcessedPending();
    if (!this.pendingPayment) return null;
    const pendingRequest = this.pendingPayment.request;
    const pendingRequestId = pendingRequest.request_id;
    const capturePendingRevision = () => ({
      record: this.pendingPayment,
      revision: Number(this.pendingPayment?.revision) || 0,
    });
    const hasPendingAdvanced = (snapshot) => (
      !this.pendingPayment
      || this.pendingPayment !== snapshot.record
      || (Number(this.pendingPayment.revision) || 0) !== snapshot.revision
    );
    const supersededRecoveryResult = () => {
      if (
        !this.pendingPayment
        || this.pendingPayment.request.request_id !== pendingRequestId
      ) {
        return {
          status: "completed",
          outcome: "reconciled_elsewhere",
          request_id: pendingRequestId,
        };
      }
      return {
        status: this.pendingPayment.state,
        outcome: "state_advanced",
        request_id: pendingRequestId,
      };
    };
    if (this.pendingPayment.state === "awaiting_ack") {
      const requestId = pendingRequestId;
      if (this.retryAcknowledgement(requestId)) {
        return { status: "acknowledged", request_id: requestId };
      }
      if (NON_APPROVED_TERMINAL_STATUSES.has(this.pendingPayment?.terminalStatus)) {
        this.finalizeProcessedRequest(requestId);
        return {
          status: "completed",
          outcome: "terminal_failure_ack_unavailable",
          request_id: requestId,
        };
      }

      // Pending records created before terminal_status was persisted can still be unblocked when
      // Android confirms that the original operation ended without a charge. Never apply this
      // fallback to approved, pending, unknown or uncorrelated results.
      const getCardPaymentState = this.getWindow()?.AndroidBridge?.getCardPaymentState;
      if (typeof getCardPaymentState === "function") {
        const recoverySnapshot = capturePendingRevision();
        try {
          const rawResult = await Promise.resolve(
            getCardPaymentState.call(this.getWindow().AndroidBridge, requestId)
          );
          if (hasPendingAdvanced(recoverySnapshot)) return supersededRecoveryResult();
          const result = normalizeKinposResult(
            rawResult,
            pendingRequest,
            { trustedQuery: true }
          );
          if (NON_APPROVED_TERMINAL_STATUSES.has(result?.status)) {
            this.pendingPayment.terminalStatus = result.status;
            this.persistPendingPayment();
            this.finalizeProcessedRequest(requestId);
            return {
              status: "completed",
              outcome: "terminal_failure_recovered",
              request_id: requestId,
            };
          }
        } catch (error) {
          if (hasPendingAdvanced(recoverySnapshot)) return supersededRecoveryResult();
          // Keep awaiting_ack when Android cannot prove that the operation ended without a charge.
        }
      }
      return { status: "awaiting_ack", request_id: requestId };
    }
    if (
      this.pendingPayment.state === "awaiting_registration"
      && pendingRequest.operation !== "close"
    ) {
      return { status: "awaiting_registration", request_id: pendingRequestId };
    }
    const getCardPaymentState = this.getWindow()?.AndroidBridge?.getCardPaymentState;
    if (typeof getCardPaymentState !== "function") {
      this.pendingPayment.state = "uncertain";
      this.persistPendingPayment();
      const result = this.attachPendingContext({
        status: "uncertain",
        outcome: "recovery_unavailable",
        request_id: pendingRequestId,
        message: "Android no expone consulta de estado para este pago.",
      });
      this.notify(result);
      return result;
    }

    let recoverySnapshot;
    try {
      if (
        this.pendingPayment.state === "registration_failed"
        || (
          this.pendingPayment.state === "awaiting_registration"
          && pendingRequest.operation === "close"
        )
      ) {
        this.pendingPayment.delivered = false;
        this.pendingPayment.terminalResult = null;
      }
      recoverySnapshot = capturePendingRevision();
      const rawResult = await Promise.resolve(
        getCardPaymentState.call(this.getWindow().AndroidBridge, pendingRequestId)
      );
      if (hasPendingAdvanced(recoverySnapshot)) return supersededRecoveryResult();
      const result = normalizeKinposResult(rawResult, pendingRequest, { trustedQuery: true });
      if (!result) {
        this.pendingPayment.state = "uncertain";
        this.persistPendingPayment();
        const uncertainResult = this.attachPendingContext({
          status: "uncertain",
          outcome: "not_found",
          request_id: pendingRequestId,
          message: "Android no pudo confirmar el resultado del pago.",
        });
        this.notify(uncertainResult);
        return uncertainResult;
      }
      return this.handleNormalizedResult(result);
    } catch (error) {
      if (recoverySnapshot && hasPendingAdvanced(recoverySnapshot)) {
        return supersededRecoveryResult();
      }
      this.pendingPayment.state = "uncertain";
      this.persistPendingPayment();
      const result = this.attachPendingContext({
        status: "uncertain",
        outcome: "recovery_error",
        request_id: pendingRequestId,
        message: "No fue posible consultar el resultado del pago.",
      });
      this.notify(result);
      return result;
    }
  }

  markAwaitingRegistration(requestId) {
    if (!this.pendingPayment || this.pendingPayment.request.request_id !== requestId) return false;
    this.pendingPayment.state = "awaiting_registration";
    this.pendingPayment.terminalResult = null;
    this.persistPendingPayment();
    return true;
  }

  markProcessingFailed(requestId) {
    if (!this.pendingPayment || this.pendingPayment.request.request_id !== requestId) return false;
    this.pendingPayment.state = "registration_failed";
    this.pendingPayment.delivered = true;
    this.persistPendingPayment();
    return true;
  }

  complete(requestId) {
    if (!this.pendingPayment || this.pendingPayment.request.request_id !== requestId) return false;
    if (this.isProcessed(requestId)) return false;
    this.pendingPayment.state = "awaiting_ack";
    this.persistPendingPayment();
    if (this.retryAcknowledgement(requestId)) return true;
    if (NON_APPROVED_TERMINAL_STATUSES.has(this.pendingPayment?.terminalStatus)) {
      return this.finalizeProcessedRequest(requestId);
    }
    return false;
  }

  // Salida de emergencia para un pago pendiente que quedo ATRAPADO en este dispositivo (ej. un pago mixto cuya orden
  // nunca se pudo registrar/finalizar y se restaura en cada apertura de la app). Lo marca como procesado (asi no se
  // vuelve a restaurar ni a entregar un resultado repetido), intenta el ACK a Android (mejor esfuerzo) y borra el
  // pendiente persistido. NO reversa nada en la tarjeta: la conciliacion del cobro es manual y de quien lo descarta
  // (la UI lo avisa y deja constancia local). Solo funciona para el pendiente actual (mismo request_id).
  discardPendingPayment(requestId) {
    if (!this.pendingPayment || this.pendingPayment.request.request_id !== requestId) return false;
    try {
      const ackCardPaymentResult = this.getWindow()?.AndroidBridge?.ackCardPaymentResult;
      if (typeof ackCardPaymentResult === "function") {
        ackCardPaymentResult.call(this.getWindow().AndroidBridge, requestId);
      }
    } catch (error) {
      // El ACK es mejor esfuerzo: el descarte local no depende de que Android responda.
    }
    this.processedRequestIds.add(requestId);
    this.persistProcessedRequests();
    this.clearPendingPayment();
    return true;
  }

  finalizeProcessedRequest(requestId) {
    if (!this.pendingPayment || this.pendingPayment.request.request_id !== requestId) return false;
    this.processedRequestIds.add(requestId);
    this.persistProcessedRequests();
    this.clearPendingPayment();
    return true;
  }

  retryAcknowledgement(requestId) {
    if (!this.pendingPayment || this.pendingPayment.request.request_id !== requestId) return false;
    const ackCardPaymentResult = this.getWindow()?.AndroidBridge?.ackCardPaymentResult;
    if (typeof ackCardPaymentResult !== "function") {
      this.pendingPayment.state = "awaiting_ack";
      this.persistPendingPayment();
      return false;
    }
    try {
      const acknowledged = ackCardPaymentResult.call(this.getWindow().AndroidBridge, requestId);
      if (acknowledged !== true) {
        this.pendingPayment.state = "awaiting_ack";
        this.persistPendingPayment();
        return false;
      }
    } catch (error) {
      this.pendingPayment.state = "awaiting_ack";
      this.persistPendingPayment();
      return false;
    }
    return this.finalizeProcessedRequest(requestId);
  }

  clearPendingPayment() {
    if (this.pendingPayment) clearTimeout(this.pendingPayment.timeoutId);
    this.pendingPayment = null;
    this.removePersistedPendingPayment();
    this.notifyPendingState();
  }
}

export const kinposPaymentBridge = new KinposPaymentBridge();
